import React, { useContext, useRef, useState, useEffect } from "react"
import { useCamp } from "~/api2/hooks/useCamp"
import { useOrders } from "~/hooks/useOrders"
import { titleCase } from "~/utils"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import { DateTime } from "luxon"
import { AdminContext } from "~/contexts/AdminContext"
import classNames from "~/utils/classNames"
import Spinner from "~/components/ui/Spinner"
import { useReactToPrint } from "react-to-print"
import OrderSummary from "~/components/Admin/Orders/OrderSummary"
import { paymentModeFormatter } from "~/utils/paymentModeFormatter"
import Pagination from "~/ComponentsLibrary/Pagination"

interface StoreHistoryProps {
  setSelectedOrder?: (order) => void;
  selectedOrderId?: string;
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  perPage: number
}

const StoreHistory: React.FC<StoreHistoryProps> = ({ setSelectedOrder, selectedOrderId, page, setPage, perPage }) => {
  const { index: { data: camp } } = useCamp()
  const { index: { data: orders, error, isLoading, totalPages, totalCount } } = useOrders({
    campId: camp?.id,
    isStore: true,
    page,
    perPage
  })

  const [orderForPrint, setOrderForPrint] = useState(null)
  const [isPrintLoading, setIsPrintLoading] = useState(false)

  const receiptPrintRef = useRef(null)
  const usePrint = useReactToPrint({
    content: () => receiptPrintRef.current,
    onAfterPrint: () => {
      setOrderForPrint(null)
      setIsPrintLoading(false)
    },
  })

  useEffect(() => {
    if (orderForPrint?.id) {
      setIsPrintLoading(true)
      usePrint(undefined)
    }
  }, [orderForPrint])

  if (isLoading) {
    return (
      <div className="w-full h-56 flex flex-col items-center justify-center p-4 space-y-3">
        <Spinner size={6} />
        <span className="text-lg text-gray-500 animate-pulse">Loading store history...</span>
      </div>
    );
  }
  if (error) return <div className="w-full p-4 text-red-600">{JSON.stringify(error)}</div>

  const sortedOrders = orders?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  return (
    <div className="w-full overflow-hidden">
      <div className="mb-4">
        <div className="text-xl font-semibold text-gray-900">Store Order History</div>
        <p className="text-sm text-gray-500">Click on an order to view details</p>
      </div>

      {/* Desktop view - Table */}
      <div className="w-full hidden md:block pr-1">
        <Pagination totalRecords={totalCount} totalPages={totalPages} page={page} setPage={setPage} perPage={perPage} />

        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th scope="col" className="py-3.5 pl-2 pr-3 text-left text-md font-normal text-gray-500">
                Created on
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-md font-normal text-gray-500">
                Guest
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-md font-normal text-gray-500">
                Amount
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-md font-normal text-gray-500">
                Status
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-md font-normal text-gray-500">
                Method
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {!sortedOrders || sortedOrders.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-300">
                  No store orders found
                </td>
              </tr>
            ) : (
              sortedOrders.map((order) => (
                <tr
                  key={`desktop-${order.id}`}
                  className={classNames(
                    "cursor-pointer hover:bg-gray-50 transition-colors",
                    selectedOrderId === order.id ? "bg-blue-50" : "",
                  )}
                  onClick={() => setSelectedOrder && setSelectedOrder(order)}
                >
                  <td className="py-4 pl-3 pr-3 text-md font-medium text-gray-800">
                    {DateTime.fromJSDate(new Date(order.created_at), {
                      zone: camp.timezone,
                    }).toFormat("LLL d, yyyy h:mm a")}
                  </td>
                  <td className="px-3 py-4 text-md text-gray-800">
                    {order.camper?.first_name || ""} {order.camper?.last_name || ""}
                  </td>
                  <td className="px-3 py-4 text-md text-gray-800 content-start">
                    <>
                      {formatMoneyFromCents(order.total_in_cents, camp?.currency)}
                      {order.payments?.length === 1 && order.payments[0].refunds?.length ? (
                        <span className="ml-2 text-gray-400">
                          (
                          {formatMoneyFromCents(
                            order.payments[0].refunds.reduce((sum, { amount_in_cents }) => sum + amount_in_cents, 0),
                            camp?.currency,
                          )}{" "}
                          refunded)
                        </span>
                      ) : (
                        ""
                      )}
                    </>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-md text-gray-800">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${order.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                    >
                      {titleCase(order.status)}
                    </span>
                  </td>
                  {order.payments?.length > 0 ? (
                    <td className="px-3 py-4 text-md text-gray-800">
                      <span className="text-sm font-medium">{paymentModeFormatter(order.payments[0].mode)}</span>
                    </td>
                  ) : (
                    <td className="px-3 py-4"></td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile view - Card layout */}
      <div className="w-full md:hidden">
        {!sortedOrders || sortedOrders.length === 0 ? (
          <div className="text-center py-4 text-gray-300">No store orders found</div>
        ) : (
          <div className={classNames("space-y-2.5 overflow-y-auto", selectedOrderId ? "max-h-[50vh]" : "")}>
            {sortedOrders.map((order) => {
              const hasGuestName = !!(order.camper?.first_name || order.camper?.last_name)

              return (
                <div
                  key={`mobile-${order.id}`}
                  className={classNames(
                    "cursor-pointer border border-gray-200 rounded-md p-3 shadow-sm transition-colors",
                    selectedOrderId === order.id ? "bg-emerald-50 border-green-600 border-1.5" : "bg-white",
                  )}
                  onClick={() => setSelectedOrder && setSelectedOrder(order)}
                >
                  <div className="flex justify-between items-start mb-1">
                    {hasGuestName ? (
                      <div className="text-md font-medium text-gray-800">
                        {order.camper?.first_name || ""} {order.camper?.last_name || ""}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {DateTime.fromJSDate(new Date(order.created_at), {
                          zone: camp.timezone,
                        }).toFormat("LLL d, yyyy h:mm a")}
                      </div>
                    )}
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${order.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                    >
                      {titleCase(order.status)}
                    </span>
                  </div>

                  {hasGuestName && (
                    <div className="text-sm text-gray-500">
                      {DateTime.fromJSDate(new Date(order.created_at), {
                        zone: camp.timezone,
                      }).toFormat("LLL d, yyyy h:mm a")}
                    </div>
                  )}

                  <div className="flex justify-between mt-1">
                    <div className="text-md font-medium">
                      {formatMoneyFromCents(order.total_in_cents, camp?.currency)}
                      {order.payments?.length === 1 && order.payments[0].refunds?.length ? (
                        <span className="ml-2 text-sm text-gray-400">
                          (
                          {formatMoneyFromCents(
                            order.payments[0].refunds.reduce((sum, { amount_in_cents }) => sum + amount_in_cents, 0),
                            camp?.currency,
                          )}{" "}
                          refunded)
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                    {order.payments?.length > 0 && (
                      <div className="text-sm text-gray-500">{paymentModeFormatter(order.payments[0].mode)}</div>
                    )}
                  </div>
                </div>
              )
            })}
            <Pagination
              totalRecords={totalCount}
              totalPages={totalPages}
              page={page}
              setPage={setPage}
              perPage={perPage}
            />
          </div>
        )}
        <div className="border-b border-gray-200 my-5" />
      </div>

      <div className="w-full mt-3 border-t border-gray-200 hidden">
        <div className="m-8" ref={receiptPrintRef}>
          <OrderSummary order={orderForPrint} camp={camp} isPreview isIncludingLineItems={true} />
        </div>
      </div>
    </div>
  )
}

export default StoreHistory
